import React, { useEffect, useState } from 'react';
import {
  Eye,
  EyeSlash,
  WarningCircle,
  CheckCircle,
  Buildings,
  UserCircle,
} from '@phosphor-icons/react';
import { changePasswordRequest, updateBrandingRequest } from '../../api/client';
import { BrandingEditor } from '../../components/account/BrandingEditor';
import { useAuth } from '../../context/AuthContext';
import { SectionCard } from '../../components/dashboard/SectionCard';
import { AccountStatusBadge } from '../../components/account/AccountStatusBadge';
import { cn } from '../../lib/utils';

const DEFAULT_COLOR = '#1A428A';

function PasswordField({
  id,
  label,
  value,
  onChange,
  showPassword,
  onToggleShow,
  autoComplete,
  hint,
}) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500"
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          name={id}
          type={showPassword ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
          className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 pr-11 text-sm text-slate-800 outline-none transition focus:border-[var(--brand-primary,#1A428A)] focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/15"
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 transition hover:text-slate-600"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? (
            <EyeSlash size={16} weight="bold" aria-hidden="true" />
          ) : (
            <Eye size={16} weight="bold" aria-hidden="true" />
          )}
        </button>
      </div>
      {hint ? <p className="text-[11px] text-slate-500">{hint}</p> : null}
    </div>
  );
}

function StatusBanner({ variant, children }) {
  const styles = {
    warning: 'border-[#C9A227]/40 bg-[#F8F1E3] text-[#5D2E17]',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    error: 'border-rose-200 bg-rose-50 text-rose-800',
  };
  const Icon = variant === 'success' ? CheckCircle : WarningCircle;

  return (
    <div className={cn('flex gap-3 rounded-xl border px-4 py-3 text-sm', styles[variant])}>
      <Icon size={16} weight="fill" className="mt-0.5 shrink-0" aria-hidden="true" />
      <div>{children}</div>
    </div>
  );
}

function ActionButton({ children, disabled, type = 'button', onClick }) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-9 items-center justify-center rounded-full bg-[var(--brand-primary,#1A428A)] px-4 text-sm font-semibold text-white shadow-md transition duration-150 hover:scale-105 hover:bg-[#163872] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/20 disabled:pointer-events-none disabled:opacity-50 disabled:hover:scale-100"
    >
      {children}
    </button>
  );
}

export function AccountSettingsPage() {
  const { user, isSuperAdmin, updateUser, refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const [primaryColor, setPrimaryColor] = useState(user?.primaryColor || DEFAULT_COLOR);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [brandingSubmitting, setBrandingSubmitting] = useState(false);
  const [brandingError, setBrandingError] = useState(null);
  const [brandingSuccess, setBrandingSuccess] = useState(false);

  useEffect(() => {
    setPrimaryColor(user?.primaryColor || DEFAULT_COLOR);
  }, [user?.primaryColor]);

  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    };
  }, [logoPreview]);

  function handleLogoChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return URL.createObjectURL(file);
    });
    setBrandingSuccess(false);
  }

  async function handleBrandingSubmit(event) {
    event.preventDefault();
    setBrandingError(null);
    setBrandingSuccess(false);

    const colorUnchanged = primaryColor === (user?.primaryColor || DEFAULT_COLOR);
    if (!logoFile && colorUnchanged) {
      setBrandingError('Choose a new logo or brand color before saving.');
      return;
    }

    setBrandingSubmitting(true);
    try {
      await updateBrandingRequest({
        logoFile,
        primaryColor,
      });
      await refreshUser({ bustLogo: true });
      setLogoFile(null);
      setLogoPreview((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
      setBrandingSuccess(true);
    } catch (submitError) {
      setBrandingError(submitError.message || 'Unable to update branding.');
    } finally {
      setBrandingSubmitting(false);
    }
  }

  async function handlePasswordSubmit(event) {
    event.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    setPasswordSubmitting(true);
    try {
      await changePasswordRequest({
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSuccess(true);
      updateUser({ mustResetPassword: false });
    } catch (submitError) {
      setPasswordError(submitError.message || 'Unable to update password.');
    } finally {
      setPasswordSubmitting(false);
    }
  }

  const accountEmail = user?.email || user?.username;
  const brandingDirty =
    Boolean(logoFile) || primaryColor !== (user?.primaryColor || DEFAULT_COLOR);
  const roleLabel = isSuperAdmin ? 'Platform admin' : 'Account user';
  const brandSwatch = user?.primaryColor || DEFAULT_COLOR;
  const identityLogo = logoPreview || user?.logoUrl;

  return (
    <div className="flex w-full min-w-0 flex-col gap-5">
      {user?.mustResetPassword ? (
        <StatusBanner variant="warning">
          <p className="font-semibold">Replace your temporary password</p>
          <p className="mt-1 text-[12px] leading-5 opacity-90">
            You signed in with an auto-generated password. Choose a new one to secure your account.
          </p>
        </StatusBanner>
      ) : null}

      {/* Identity first */}
      <section className="flex min-w-0 flex-col gap-4 rounded-xl border border-slate-200/90 bg-white p-4 shadow-[0_4px_16px_rgba(15,23,42,0.08)] sm:flex-row sm:items-center sm:gap-5 sm:p-5">
        <div
          className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-[#F7F9FC]"
          style={{ boxShadow: `inset 0 0 0 2px ${brandSwatch}22` }}
        >
          {identityLogo ? (
            <img src={identityLogo} alt="" className="h-full w-full object-contain p-1.5" />
          ) : (
            <UserCircle size={36} weight="duotone" className="text-slate-400" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display truncate text-base font-bold text-[#201C35]">
              {user?.displayName || 'Account user'}
            </h2>
            <AccountStatusBadge status="active" />
          </div>
          <p className="mt-1 break-all text-sm text-slate-600">{accountEmail}</p>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-black/5"
                style={{ backgroundColor: brandSwatch }}
                aria-hidden="true"
              />
              {brandSwatch}
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">
              {roleLabel}
            </span>
            {user?.accountName ? (
              <span className="inline-flex items-center gap-1">
                <Buildings size={12} weight="bold" aria-hidden="true" />
                {user.accountName}
              </span>
            ) : null}
          </div>
        </div>
      </section>

      <div className={cn('grid min-w-0 gap-5', !isSuperAdmin && 'lg:grid-cols-2')}>
        {!isSuperAdmin ? (
          <SectionCard
            title="Dashboard branding"
            description="Logo and brand color used across your dashboard."
          >
            <div className="flex flex-col gap-4">
              {brandingSuccess ? (
                <StatusBanner variant="success">
                  <p>Your dashboard branding has been updated.</p>
                </StatusBanner>
              ) : null}
              {brandingError ? (
                <StatusBanner variant="error">
                  <p>{brandingError}</p>
                </StatusBanner>
              ) : null}

              <form className="space-y-4" onSubmit={handleBrandingSubmit}>
                <BrandingEditor
                  primaryColor={primaryColor}
                  onPrimaryColorChange={(value) => {
                    setPrimaryColor(value);
                    setBrandingSuccess(false);
                  }}
                  logoPreview={logoPreview}
                  currentLogoUrl={user?.logoUrl}
                  onLogoChange={handleLogoChange}
                  disabled={brandingSubmitting}
                />
                <div className="flex justify-end border-t border-slate-100 pt-4">
                  <ActionButton type="submit" disabled={brandingSubmitting || !brandingDirty}>
                    {brandingSubmitting ? 'Saving…' : 'Save branding'}
                  </ActionButton>
                </div>
              </form>
            </div>
          </SectionCard>
        ) : null}

        <SectionCard
          title="Sign-in security"
          description={`Update the password for ${accountEmail}.`}
        >
          <div className="flex flex-col gap-4">
            {passwordSuccess ? (
              <StatusBanner variant="success">
                <p>Your password has been updated successfully.</p>
              </StatusBanner>
            ) : null}
            {passwordError ? (
              <StatusBanner variant="error">
                <p>{passwordError}</p>
              </StatusBanner>
            ) : null}

            <form className="space-y-4" onSubmit={handlePasswordSubmit}>
              <PasswordField
                id="current-password"
                label="Current password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                showPassword={showCurrentPassword}
                onToggleShow={() => setShowCurrentPassword((value) => !value)}
                autoComplete="current-password"
              />
              <PasswordField
                id="new-password"
                label="New password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                showPassword={showNewPassword}
                onToggleShow={() => setShowNewPassword((value) => !value)}
                autoComplete="new-password"
                hint="Use at least 8 characters."
              />
              <PasswordField
                id="confirm-password"
                label="Confirm new password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                showPassword={showConfirmPassword}
                onToggleShow={() => setShowConfirmPassword((value) => !value)}
                autoComplete="new-password"
              />
              <div className="flex justify-end border-t border-slate-100 pt-4">
                <ActionButton
                  type="submit"
                  disabled={
                    passwordSubmitting || !currentPassword || !newPassword || !confirmPassword
                  }
                >
                  {passwordSubmitting ? 'Updating…' : 'Update password'}
                </ActionButton>
              </div>
            </form>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
